import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './MyShips.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MyShips = () => {
  const navigate = useNavigate();
  const [ships, setShips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedShip, setSelectedShip] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [otherType, setOtherType] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [availableShipTypes] = useState([
    'Container Ship', 'Oil Tanker', 'LNG Carrier', 'Bulk Carrier',
    'Passenger Ship', 'Tug Boat', 'Offshore Vessel', 'Chemical Tanker', 
    'Ro-Ro Ship', 'Others'
  ]);
  
  const [formData, setFormData] = useState({
    name: '', imo: '', type: '', flag: '', built: '', grt: '', dwt: '', status: 'active'
  });

  const getToken = () => localStorage.getItem('marilink_token');

  const fetchVessels = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      if (!token) {
        setErrorMessage('Authentication required. Please login again.');
        return;
      }
      
      const response = await fetch(`${API_BASE}/vessels`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setShips(data.vessels || []);
        setErrorMessage('');
      } else {
        setErrorMessage(data.message || 'Failed to fetch vessels');
      }
    } catch (error) {
      setErrorMessage('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchVessels(); 
  }, []);

  const isImoDuplicate = (imo) => {
    if (!imo) return false;
    return ships.some(ship => ship.imo === imo && ship._id !== selectedShip?._id);
  };

  const getFinalType = () => {
    return formData.type === 'Others' ? otherType : formData.type;
  };

  const validateForm = () => {
    const finalType = getFinalType();
    
    if (!formData.name.trim()) {
      alert('Ship name is required');
      return false;
    }
    if (!formData.imo.trim()) {
      alert('IMO number is required');
      return false;
    }
    if (formData.imo.length !== 7 || !/^\d+$/.test(formData.imo)) {
      alert('IMO number must be exactly 7 digits');
      return false;
    }
    if (!finalType || finalType.trim() === '') {
      alert('Ship type is required');
      return false;
    }
    if (!formData.flag.trim()) {
      alert('Flag/Registry is required');
      return false;
    }
    if (!formData.grt || parseFloat(formData.grt) <= 0) {
      alert('Gross Tonnage (GRT) must be greater than 0');
      return false;
    }
    if (isImoDuplicate(formData.imo)) {
      alert(`IMO number ${formData.imo} is already registered`);
      return false;
    }
    
    return true;
  };

  const handleAddShip = async () => {
    setErrorMessage('');
    
    if (!validateForm()) return;
    
    const finalType = getFinalType();
    
    const payload = {
      name: formData.name.trim(),
      imo: formData.imo.trim(),
      type: finalType.trim(),
      flag: formData.flag.trim(),
      grt: parseInt(formData.grt),
      dwt: formData.dwt ? parseInt(formData.dwt) : null,
      built: formData.built ? parseInt(formData.built) : null,
      status: formData.status
    };
    
    try {
      const token = getToken();
      
      const response = await fetch(`${API_BASE}/vessels`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchVessels();
        setShowAddModal(false);
        resetForm();
      } else {
        setErrorMessage(data.message);
      }
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const handleEditShip = async () => {
    setErrorMessage('');
    
    const finalType = getFinalType();
    
    if (!finalType || finalType.trim() === '') {
      alert('Ship type is required');
      return;
    }
    
    if (isImoDuplicate(formData.imo)) {
      alert(`IMO ${formData.imo} already exists.`);
      return;
    }
    
    const payload = {
      name: formData.name.trim(),
      imo: formData.imo.trim(),
      type: finalType.trim(),
      flag: formData.flag.trim(),
      grt: parseInt(formData.grt),
      dwt: formData.dwt ? parseInt(formData.dwt) : null,
      built: formData.built ? parseInt(formData.built) : null,
      status: formData.status
    };
    
    try {
      const token = getToken();
      
      const response = await fetch(`${API_BASE}/vessels/${selectedShip._id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchVessels();
        setShowEditModal(false);
        resetForm();
      } else {
        alert(data.message || 'Failed to update vessel');
      }
    } catch (error) {
      alert('Network error: ' + error.message);
    }
  };

  const handleDeleteShip = async (id) => {
    if (!window.confirm('Are you sure you want to remove this vessel? This action cannot be undone.')) return;
    
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/vessels/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchVessels();
      } else {
        alert(data.message || 'Failed to delete vessel');
      }
    } catch (error) {
      alert('Network error: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      imo: '', 
      type: '', 
      flag: '', 
      built: '', 
      grt: '', 
      dwt: '', 
      status: 'active' 
    });
    setOtherType('');
    setSelectedShip(null);
    setErrorMessage('');
  };

  const openEditModal = (ship) => {
    const isPredefined = availableShipTypes.includes(ship.type);
    setSelectedShip(ship);
    setFormData({
      name: ship.name,
      imo: ship.imo,
      type: isPredefined ? ship.type : 'Others',
      flag: ship.flag,
      built: ship.built || '',
      grt: ship.grt || '',
      dwt: ship.dwt || '',
      status: ship.status || 'active'
    });
    if (!isPredefined) {
      setOtherType(ship.type);
    } else {
      setOtherType('');
    }
    setShowEditModal(true);
  };

  const filteredShips = ships.filter(ship => 
    ship.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    ship.imo?.includes(searchTerm)
  );

  if (loading) return (
    <div className="loader-container">
      <i className="fa-solid fa-spinner fa-spin"></i>
      <span>Loading vessels...</span>
    </div>
  );

  return (
    <div className="myships-wrapper">
      <div className="fleet-hero">
        <div className="hero-content">
          <h1>Fleet Overview</h1>
          <p>Manage and monitor your registered vessels</p>
        </div>
        <button className="add-vessel-btn" onClick={() => setShowAddModal(true)}>
          <i className="fa-solid fa-plus"></i> <span>Add Vessel</span>
        </button>
      </div>

      <div className="control-bar">
        <div className="search-pill">
          <i className="fa-solid fa-search"></i>
          <input 
            type="text" 
            placeholder="Search name or IMO..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="fleet-count">{filteredShips.length} Vessels Found</div>
      </div>

      {errorMessage && (
        <div className="error-banner">
          <i className="fa-solid fa-circle-exclamation"></i>
          <span>{errorMessage}</span>
          <button onClick={fetchVessels}>Retry</button>
        </div>
      )}

      <div className="vessel-grid-container">
        <table className="vessel-table-modern">
          <thead>
            <tr>
              <th>Vessel Identity</th>
              <th>Type</th>
              <th className="hide-mobile">Flag</th>
              <th className="hide-mobile">Tonnage</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredShips.length > 0 ? (
              filteredShips.map(ship => (
                <tr key={ship._id}>
                  <td>
                    <div className="v-identity">
                      <span className="v-primary-name">{ship.name}</span>
                      <span className="v-secondary-imo">IMO {ship.imo}</span>
                    </div>
                   </td>
                  <td><span className="v-badge">{ship.type}</span></td>
                  <td className="hide-mobile"><span className="v-flag-info">{ship.flag}</span></td>
                  <td className="hide-mobile"><span className="v-ton-info">{ship.grt?.toLocaleString()} <small>GT</small></span></td>
                  <td><div className={`status-indicator ${ship.status}`}>{ship.status}</div></td>
                  <td className="text-right">
                    <div className="action-flex">
                      <button className="action-circle" onClick={() => navigate(`/owner/ship/${ship._id}`)}>
                        <i className="fa-regular fa-eye"></i>
                      </button>
                      <button className="action-circle" onClick={() => openEditModal(ship)}>
                        <i className="fa-regular fa-pen-to-square"></i>
                      </button>
                      <button className="action-circle del" onClick={() => handleDeleteShip(ship._id)}>
                        <i className="fa-regular fa-trash-can"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="empty-row">
                  {searchTerm ? 'No matching vessels found.' : 'No vessels registered yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(showAddModal || showEditModal) && (
        <div className="glass-overlay" onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>
          <div className="vessel-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-top">
              <h3>{showAddModal ? 'Register New Vessel' : 'Update Vessel Data'}</h3>
              <button className="close-x" onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="grid-row">
                <div className="input-box">
                  <label>Vessel Name *</label>
                  <input 
                    type="text" 
                    value={formData.name} 
                    onChange={e => setFormData({...formData, name: e.target.value})} 
                    placeholder="e.g. MV Aurora" 
                  />
                </div>
                <div className="input-box">
                  <label>IMO Number *</label>
                  <input 
                    type="text" 
                    maxLength="7" 
                    value={formData.imo} 
                    onChange={e => setFormData({...formData, imo: e.target.value.replace(/\D/g, '')})} 
                    placeholder="7 Digits" 
                  />
                  {isImoDuplicate(formData.imo) && (
                    <span style={{color:'#ef4444', fontSize:'10px', marginTop:'4px', display:'block'}}>
                      IMO already registered
                    </span>
                  )}
                </div>
              </div>
              
              <div className="grid-row">
                <div className="input-box">
                  <label>Ship Type *</label>
                  <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}>
                    <option value="">Select Type</option>
                    {availableShipTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="input-box">
                  <label>Flag / Registry *</label>
                  <input 
                    type="text" 
                    value={formData.flag} 
                    onChange={e => setFormData({...formData, flag: e.target.value})} 
                    placeholder="e.g. Panama" 
                  />
                </div>
              </div>

              {formData.type === 'Others' && (
                <div className="grid-row" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="input-box">
                    <label>Specify Ship Type *</label>
                    <input 
                      type="text" 
                      value={otherType} 
                      onChange={e => setOtherType(e.target.value)} 
                      placeholder="Enter custom vessel type"
                      autoFocus 
                    />
                  </div>
                </div>
              )}

              <div className="grid-row">
                <div className="input-box">
                  <label>Gross Tonnage (GRT) *</label>
                  <input 
                    type="number" 
                    value={formData.grt} 
                    onChange={e => setFormData({...formData, grt: e.target.value})} 
                    placeholder="e.g. 45000" 
                    min="1"
                  />
                </div>
                <div className="input-box">
                  <label>Year Built</label>
                  <input 
                    type="number" 
                    value={formData.built} 
                    onChange={e => setFormData({...formData, built: e.target.value})} 
                    placeholder="YYYY" 
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-btns">
              <button className="btn-secondary" onClick={() => { setShowAddModal(false); setShowEditModal(false); resetForm(); }}>
                Cancel
              </button>
              <button 
                className="btn-primary-brand" 
                onClick={showAddModal ? handleAddShip : handleEditShip}
                disabled={isImoDuplicate(formData.imo)}
              >
                {showAddModal ? 'Confirm Registration' : 'Save Updates'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyShips;